import { api, API_BASE, request } from "./client";
import type { ApiMessageAttachment } from "./types";
import type { MessageAttachment } from "../types";

// Chat attachment uploads (`api.md` §Chat attachments; the composer
// contract in `chat.md` §Composer UI). Two-phase: a file dropped on the
// composer uploads here immediately — per-file progress, failures surfaced
// before send — and the message claims the finished uploads by public id on
// send (`sendChatMessage`).

export type ApiAttachment = {
  readonly bytes: number;
  readonly mime: string;
  readonly name: string;
  readonly public_id: string;
  readonly sha256: string;
};

/** Per-file cap; mirrors the server's route body limit. */
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

/** Per-message cap; mirrors the claim's limit in the message POST. */
export const MAX_ATTACHMENTS_PER_MESSAGE = 10;

/**
 * The image types the thread previews inline. Everything outside these and the
 * video types below renders as a download chip.
 *
 * SVG is the one that needs saying, because it *is* a scriptable document. It
 * is previewed anyway, and only ever through an `<img>`: that renders it in
 * the HTML spec's secure static mode, where no script runs and no external
 * reference resolves. An HTML file is still a chip — nothing renders one
 * without executing it (`attachments.md` §Decisions 6).
 */
const IMAGE_MIMES = new Set([
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/svg+xml",
  "image/webp",
]);

/**
 * The video types the thread plays inline.
 *
 * The same reasoning admits them: a `<video>` decodes bytes and runs nothing,
 * exactly as an `<img>` does, so the rule that keeps scriptable documents out
 * is untouched by letting a film in. What is *not* the same is certainty — a
 * container is not a codec, and a `.mov` full of ProRes decodes nowhere. The
 * player says so and the caller falls back to the chip (`ThreadMedia`).
 */
const VIDEO_MIMES = new Set(["video/mp4", "video/quicktime", "video/webm"]);

/**
 * Whether the thread shows this file itself, and as what. Null is a download
 * chip — the answer for everything the two sets above do not name.
 */
export const previewKind = (mime: string): "image" | "video" | null =>
  IMAGE_MIMES.has(mime) ? "image" : VIDEO_MIMES.has(mime) ? "video" : null;

/**
 * Upload one file's bytes. The request body IS the file — raw, not multipart,
 * not base64 — with its MIME type as `Content-Type` and the filename in the
 * query. A pasted screenshot arrives with no name; it gets one, since the
 * name is the label on the chip and on the agent's disk.
 */
export const uploadAttachment = (
  workspaceId: string,
  file: File,
): Promise<ApiAttachment> =>
  request<{ attachment: ApiAttachment }>("/attachments", {
    contentType: file.type || "application/octet-stream",
    method: "POST",
    query: { name: file.name || "pasted-image.png", workspace_id: workspaceId },
    rawBody: file,
  }).then((r) => r.attachment);

/** A posted message's attachment row, snake_case off the wire, as the view model wants it. */
export const mapAttachment = (a: ApiMessageAttachment): MessageAttachment => ({
  bytes: a.bytes,
  mime: a.mime,
  name: a.name,
  publicId: a.public_id,
});

/** Remove a pending upload — the composer chip's ✕. Claimed ones refuse. */
export const deleteAttachment = (publicId: string): Promise<unknown> =>
  api.del(`/attachments/${publicId}`);

/**
 * Where an attachment's bytes live, for `<img>` thumbnails and download
 * links. Served with `Content-Disposition: attachment` — navigation downloads,
 * while image decoding is unaffected.
 */
export const attachmentContentPath = (publicId: string): string =>
  `${API_BASE}/attachments/${publicId}/content`;
